import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { styles } from "../styles/publicStyles";
import SectionNav from "./SectionNav";
import EmptySection, { companyName } from "./EmptySection";
import ShowMoreButton from "./ShowMoreButton";
import { PAGE_SIZE, limitFor } from "../hooks/usePagedList";
import {
  groupByCategory,
  isListed,
  isUnlocked,
  resourceLink,
} from "../data/resources";

// Request access / Download / View, the one action Vanta shows per resource.
export const resourceAction = (resource, access) => {
  if (!isUnlocked(resource, access))
    return { label: "Request access", icon: <LockOutlinedIcon /> };
  if (resource.type === "link")
    return { label: "View", icon: <OpenInNewIcon /> };
  return { label: "Download", icon: <DownloadOutlinedIcon /> };
};

// Files a visitor can take now: not links, and not waiting on an access grant.
export const canDownload = (resource, access) =>
  resource.type !== "link" && isUnlocked(resource, access);

const whyNoCheckbox = (resource) =>
  resource.type === "link"
    ? "Links open in a new tab"
    : "Request access — the company must approve before you can download";

// The small tile before a resource's name: what kind of thing it is, or a
// lock while it needs access.
const ResourceIcon = ({ resource, locked }) => {
  const theme = useTheme();
  const t = theme.trust;
  const Icon = locked
    ? LockOutlinedIcon
    : resource.type === "link"
      ? LinkRoundedIcon
      : DescriptionOutlinedIcon;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        backgroundColor: locked ? t.paper : t.secondarySoft,
        color: locked ? t.muted : t.secondary,
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Box>
  );
};

const ResourceRow = ({
  resource,
  access,
  pageUrl,
  highlighted,
  selected,
  onToggle,
  onOpen,
  onView,
  onCopied,
}) => {
  const theme = useTheme();
  const t = theme.trust;
  const action = resourceAction(resource, access);
  const selectable = canDownload(resource, access);
  const locked = !isUnlocked(resource, access);
  // A resource opened from a direct link (or a framework's report).
  const rowRef = useRef(null);
  useEffect(() => {
    if (highlighted) rowRef.current?.scrollIntoView?.({ block: "center" });
  }, [highlighted]);
  // Quiet text buttons; Request access in the secondary colour.
  const actionSx = {
    textTransform: "none",
    borderRadius: "8px",
    whiteSpace: "nowrap",
    px: 1.25,
    color: locked ? t.secondary : t.inkSoft,
    "&:hover": {
      backgroundColor: locked ? t.secondarySoft : alpha(t.ink, 0.05),
    },
    "& .MuiButton-startIcon > *": { fontSize: "18px" },
  };

  return (
    <Stack
      ref={rowRef}
      id={`resource-${resource.id}`}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        scrollMarginTop: "96px",
        borderTop: `1px solid ${t.line}`,
        backgroundColor: highlighted
          ? theme.customColors.status.info.bg
          : "transparent",
        transition: "background-color 0.6s ease",
        "&:hover": {
          backgroundColor: highlighted ? undefined : alpha(t.ink, 0.015),
        },
        // The copy-link icon shows on hover or focus (always on touch).
        "& .copy-link": { opacity: { xs: 1, md: 0 } },
        "&:hover .copy-link, & .copy-link:focus-visible": { opacity: 1 },
      }}
    >
      <Tooltip title={selectable ? "" : whyNoCheckbox(resource)} arrow>
        <span>
          <Checkbox
            size="small"
            checked={selected}
            disabled={!selectable}
            onChange={() => onToggle(resource.id)}
            inputProps={{ "aria-label": `Select ${resource.name}` }}
          />
        </span>
      </Tooltip>
      <ResourceIcon resource={resource} locked={locked} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ sm: "center" }}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography sx={{ fontWeight: 500, fontSize: 15, minWidth: 0 }}>
              {resource.name}
            </Typography>
            <Tooltip title="Copy link" arrow>
              <IconButton
                className="copy-link"
                size="small"
                aria-label={`Copy link for ${resource.name}`}
                onClick={() => {
                  navigator.clipboard?.writeText(
                    resourceLink(pageUrl, resource.id)
                  );
                  onCopied();
                }}
                sx={{ color: t.muted, transition: `opacity 160ms ${t.ease}` }}
              >
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          {resource.description && (
            <Typography
              variant="body2"
              sx={{ color: t.muted, lineHeight: 1.5, mt: 0.25 }}
            >
              {resource.description}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ flexShrink: 0, ml: { xs: -1.25, sm: 0 } }}
        >
          {/* Files the visitor may open can also be read in the page. */}
          {selectable && onView && (
            <Button
              startIcon={<VisibilityOutlinedIcon />}
              aria-label={`View ${resource.name}`}
              onClick={() => onView(resource)}
              sx={actionSx}
            >
              View
            </Button>
          )}
          <Button
            startIcon={action.icon}
            aria-label={`${action.label} ${resource.name}`}
            onClick={() => onOpen(resource)}
            sx={actionSx}
          >
            {locked ? "Request" : action.label}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

// One category inside the list: a thin band with the select-all checkbox
// (for the files in it that can be downloaded), the name and the count,
// then its resources.
const CategoryBlock = ({ group, access, selected, onToggleAll, children }) => {
  const theme = useTheme();
  const t = theme.trust;
  const selectable = group.resources.filter((r) => canDownload(r, access));
  const chosen = selectable.filter((r) => selected.has(r.id)).length;
  const all = selectable.length > 0 && chosen === selectable.length;

  return (
    <Box
      id={`category-${group.id}`}
      component="section"
      aria-label={group.name}
      sx={{
        scrollMarginTop: "96px",
        "&:not(:first-of-type)": { borderTop: `1px solid ${t.line}` },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: { xs: 1.5, sm: 2 }, py: 1, backgroundColor: t.paper }}
      >
        <Tooltip
          title={selectable.length ? "" : "Nothing here can be downloaded yet"}
          arrow
        >
          <span>
            <Checkbox
              size="small"
              checked={all}
              indeterminate={chosen > 0 && !all}
              disabled={selectable.length === 0}
              onChange={() =>
                onToggleAll(
                  selectable.map((r) => r.id),
                  !all
                )
              }
              inputProps={{ "aria-label": `Select all in ${group.name}` }}
            />
          </span>
        </Tooltip>
        <Typography
          component="h2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 600,
            color: t.ink,
          }}
        >
          {group.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: t.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {group.resources.length}{" "}
          {group.resources.length === 1 ? "resource" : "resources"}
        </Typography>
      </Stack>
      {/* The first row has no rule: the band is above it. */}
      <Box
        sx={{ "& > [id^='resource-']:first-of-type": { borderTop: "none" } }}
      >
        {children}
      </Box>
    </Box>
  );
};

// Resources tab: categories on the left (they scroll the list), search and
// Download all on top, then one list with a band per category and a
// checkbox per file for downloading several together.
// onBulkDownload(ids) downloads those (null: every file) and resolves to true
// when the download started.
const ResourcesSection = ({
  categories,
  resources,
  access,
  pageUrl,
  linkedResourceId,
  onOpen,
  onView,
  onBulkDownload,
  onCopied,
  company,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [highlightId, setHighlightId] = useState(linkedResourceId);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return resources.filter(
      (r) =>
        (isListed(r) || r.id === linkedResourceId) &&
        (!term ||
          r.name.toLowerCase().includes(term) ||
          (r.description || "").toLowerCase().includes(term))
    );
  }, [resources, search, linkedResourceId]);
  const groups = groupByCategory(categories, shown);

  // Each category shows a page of resources at a time ("Show more"), keyed
  // by category id. Search still covers every resource.
  const [limits, setLimits] = useState({});
  const limitOf = (group) => limits[group.id] ?? PAGE_SIZE;

  // A resource opened from a direct link: its page is shown, and the row
  // scrolls into view and is highlighted for a moment.
  useEffect(() => {
    if (!linkedResourceId) return undefined;
    const group = groups.find((g) =>
      g.resources.some((r) => r.id === linkedResourceId)
    );
    if (group) {
      const index = group.resources.findIndex((r) => r.id === linkedResourceId);
      setLimits((prev) => ({
        ...prev,
        [group.id]: Math.max(prev[group.id] ?? PAGE_SIZE, limitFor(index)),
      }));
    }
    setHighlightId(linkedResourceId);
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(timer);
    // Only when the link changes, not on every search keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedResourceId]);

  const downloadable = resources.filter(
    (r) => isListed(r) && canDownload(r, access)
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (ids, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const downloadSelected = async (ids) => {
    setBusy(true);
    try {
      const started = await onBulkDownload(ids);
      if (started && ids) setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  const buttonSx = {
    borderRadius: "8px",
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  // Nothing shared (and no direct link to a shareable one): the heading and
  // an empty-state card, with nothing to search or download.
  const none = !resources.some((r) => isListed(r) || r.id === linkedResourceId);
  if (none) {
    return (
      <Box id="resources">
        <EmptySection
          icon={FolderOutlinedIcon}
          title="No resources shared yet"
          message={`${companyName(company)} has not shared any documents or links yet.`}
          company={company}
        />
      </Box>
    );
  }

  const t = theme.trust;
  return (
    <Box id="resources">
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 4 }}
        alignItems="flex-start"
      >
        <SectionNav
          title="Categories"
          label="Resource categories"
          items={groups.map((group) => ({
            id: group.id,
            name: group.name,
            count: group.resources.length,
          }))}
          elementId={(item) => `category-${item.id}`}
        />

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {/* Search across names and descriptions, and Download all. */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search documents"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setLimits({});
              }}
              inputProps={{ "aria-label": "Search resources" }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { backgroundColor: t.surface },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: t.muted }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              disabled={busy || downloadable.length === 0}
              onClick={() => downloadSelected(null)}
              sx={buttonSx}
            >
              Download all
            </Button>
          </Stack>

          <Box
            sx={{
              ...styles.mainCard,
              // clip, not hidden: rounds the corners without stopping the
              // selection bar from sticking under the nav bar.
              overflow: "clip",
            }}
          >
            {/* With files ticked: how many, and what to do with them. */}
            {selected.size > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                role="region"
                aria-label="Selected files"
                sx={{
                  position: "sticky",
                  // Under the nav bar (two rows below lg).
                  top: { xs: 104, lg: 64 },
                  zIndex: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: t.secondarySoft,
                  borderBottom: `1px solid ${t.line}`,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 500,
                    color: t.secondary,
                  }}
                >
                  {selected.size} selected
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSelected(new Set())}
                  sx={{ ...buttonSx, color: t.inkSoft }}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadOutlinedIcon />}
                  disabled={busy}
                  onClick={() => downloadSelected([...selected])}
                  sx={buttonSx}
                >
                  Download ({selected.size})
                </Button>
              </Stack>
            )}
            {groups.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: t.muted, px: 2.5, py: 4, textAlign: "center" }}
              >
                {search
                  ? `No documents match “${search.trim()}”.`
                  : "No resources shared yet."}
              </Typography>
            ) : (
              groups.map((group) => (
                <CategoryBlock
                  key={group.id}
                  group={group}
                  access={access}
                  selected={selected}
                  onToggleAll={toggleAll}
                >
                  {group.resources.slice(0, limitOf(group)).map((resource) => (
                    <ResourceRow
                      key={resource.id}
                      resource={resource}
                      access={access}
                      pageUrl={pageUrl}
                      highlighted={resource.id === highlightId}
                      selected={selected.has(resource.id)}
                      onToggle={toggle}
                      onOpen={onOpen}
                      onView={onView}
                      onCopied={onCopied}
                    />
                  ))}
                  <ShowMoreButton
                    remaining={group.resources.length - limitOf(group)}
                    label={`Show more in ${group.name}`}
                    onClick={() =>
                      setLimits((prev) => ({
                        ...prev,
                        [group.id]: limitOf(group) + PAGE_SIZE,
                      }))
                    }
                    sx={{ mt: 0, py: 1.5, borderTop: `1px solid ${t.line}` }}
                  />
                </CategoryBlock>
              ))
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default ResourcesSection;
